import { Logout, UserMultiple } from '@carbon/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { Button, EmptyState, ForbiddenState, Spinner } from '@/ui';
import { QueryErrorState } from '../api/QueryErrorState';
import { SessionLockGate } from '../shell/SessionLockGate';
import { useServices, useSession } from '../AppServicesContext';
import s from '../layouts/Layout.module.css';
import { sections } from '../registry';
import { AUTH_PATHS } from './paths';
import { isStaffRole } from './roles';

function FullPageStatus({ children }: { children: ReactNode }) {
  return <div className={s.fullPageStatus}>{children}</div>;
}

/** Только для вошедших: иначе — на вход (с возвратом), при MFA_REQUIRED — на второй фактор. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation('shell');
  const { session } = useServices();
  const { state } = useSession();
  const location = useLocation();
  const next = `${location.pathname}${location.search}`;

  if (state.status === 'loading') {
    if (state.bootstrapError) {
      return (
        <FullPageStatus>
          <QueryErrorState error={state.bootstrapError} onRetry={() => void session.bootstrap()} />
        </FullPageStatus>
      );
    }
    return (
      <FullPageStatus>
        <Spinner size="lg" label={t('session.checking')} />
      </FullPageStatus>
    );
  }
  if (state.status === 'anonymous') {
    const params = new URLSearchParams({ next });
    if (state.endReason) params.set('reason', state.endReason);
    return <Navigate to={`${AUTH_PATHS.login}?${params.toString()}`} replace />;
  }
  if (state.status === 'mfa_required') return <Navigate to={`${AUTH_PATHS.mfa}?${new URLSearchParams({ next }).toString()}`} replace />;
  /* Сервер заблокировал сессию до загрузки профиля (вкладка спала дольше политики): только экран разблокировки. */
  if (state.status === 'locked') return <SessionLockGate />;
  return <>{children}</>;
}

function hasWelcomeSection(): boolean {
  return sections.some((section) => section.area === 'public' && section.path === AUTH_PATHS.welcome.slice(1));
}

/** Портал клиники: нужна хотя бы одна клиника. Сотрудник dodoc без клиник — в бэк-офис. */
export function RequireClinic({ children }: { children: ReactNode }) {
  const { t } = useTranslation('shell');
  const { session } = useServices();
  const { state } = useSession();
  const user = state.user;
  if (!user) return null;
  if (user.memberships.length === 0) {
    if (user.globalRoles.some(isStaffRole)) return <Navigate to="/backoffice" replace />;
    /* Первый вход без клиники: регистрация клиники, личное пространство врача или ожидание приглашения (ПОРТ-10, ПОРТ-15). */
    if (hasWelcomeSection()) return <Navigate to={AUTH_PATHS.welcome} replace />;
    return (
      <FullPageStatus>
        <EmptyState
          icon={UserMultiple}
          title={t('noClinic.title')}
          description={t('noClinic.description')}
          actions={
            <Button variant="tertiary" icon={Logout} onClick={() => void session.signOut()}>
              {t('profile.signOut')}
            </Button>
          }
        />
      </FullPageStatus>
    );
  }
  return <>{children}</>;
}

/** Бэк-офис: только роли DODOC_* (MFA для них обязательна — проверяет сервер, ПОРТ-21). */
export function RequireStaff({ children }: { children: ReactNode }) {
  const { state } = useSession();
  if (!state.user?.globalRoles.some(isStaffRole)) {
    return (
      <FullPageStatus>
        <ForbiddenState />
      </FullPageStatus>
    );
  }
  return <>{children}</>;
}
